import { StateManager } from '../core/StateManager';
import { ModbusRTU } from '../modbus/ModbusRTU';
import { RequestQueue } from '../modbus/RequestQueue';
import { DRIVE_PROFILES } from '../config/profiles';
import { Terminal } from '../ui/Terminal';
import type { DriveProfile } from '../types/drive';
import type { SerialManager } from './SerialManager';

export class PollingService {
  private intervalId: ReturnType<typeof setTimeout> | null = null;
  private fakeRxTimer: ReturnType<typeof setTimeout> | null = null;
  private idleCount = 0;
  private currentInterval: number;

  private readonly BASE_INTERVAL = 1000;
  private readonly ALARM_INTERVAL = 500;
  private readonly IDLE_INTERVAL = 10000;
  private readonly IDLE_THRESHOLD = 30;

  constructor(
    private state: StateManager,
    private terminal: Terminal,
    private serialManager?: SerialManager,
    private queue?: RequestQueue,
  ) {
    this.currentInterval = this.BASE_INTERVAL;
  }

  start(intervalMs?: number): void {
    this.stop();
    this.currentInterval = intervalMs ?? this.BASE_INTERVAL;
    this.scheduleNext();
  }

  stop(): void {
    if (this.intervalId !== null) {
      clearTimeout(this.intervalId);
      this.intervalId = null;
    }
    if (this.fakeRxTimer !== null) {
      clearTimeout(this.fakeRxTimer);
      this.fakeRxTimer = null;
    }
  }

  private scheduleNext(): void {
    this.intervalId = setTimeout(() => {
      this.poll();
      this.adaptInterval();
      this.scheduleNext();
    }, this.currentInterval);
  }

  private adaptInterval(): void {
    const activeTrip = this.state.get('activeTrip');
    const alarms = this.state.get('alarms');
    const hasAlarm = Object.values(alarms).some(Boolean);

    if (activeTrip || hasAlarm) {
      this.currentInterval = this.ALARM_INTERVAL;
      this.idleCount = 0;
      return;
    }

    const targetFreq = this.state.get('targetFreq');
    const currentFreq = this.state.get('currentFreq');
    const isStable = Math.abs(targetFreq - currentFreq) < 0.5;

    if (!isStable) {
      this.currentInterval = this.BASE_INTERVAL;
      this.idleCount = 0;
      return;
    }

    this.idleCount++;
    if (this.idleCount >= this.IDLE_THRESHOLD) {
      this.currentInterval = Math.min(
        this.IDLE_INTERVAL,
        this.currentInterval + 500,
      );
    } else {
      this.currentInterval = this.BASE_INTERVAL;
    }
  }

  private poll(): void {
    const mode = this.state.get('connectionMode');
    if (mode === 'serial') {
      if (this.serialManager && this.queue) {
        void this.pollSerial();
      }
      return;
    }

    const slaveId = this.state.get('slaveId');
    const profileKey = this.state.get('selectedProfileKey');
    const profile = DRIVE_PROFILES[profileKey];
    if (!profile) return;

    const requestBuffer = ModbusRTU.buildReadRequest(slaveId, profile.regFreq, 4);
    this.terminal.log(`TX -> ${ModbusRTU.bytesToHex(requestBuffer)}`, 'tx');

    this.fakeRxTimer = setTimeout(() => {
      this.fakeRxTimer = null;
      const registers = this.state.get('registers');
      const valFreq = registers[profile.regFreq] ?? 0;
      const valCurr = registers[profile.regCurrent] ?? 0;
      const valDC = registers[profile.regDC] ?? 0;
      const valTemp = registers[profile.regTemp] ?? 0;

      const rxPayload = new Uint8Array(3 + 8 + 2);
      rxPayload[0] = slaveId;
      rxPayload[1] = 0x03;
      rxPayload[2] = 0x08;

      const vals = [valFreq, valCurr, valDC, valTemp];
      let idx = 3;
      vals.forEach((v) => {
        rxPayload[idx] = (v >> 8) & 0xff;
        rxPayload[idx + 1] = v & 0xff;
        idx += 2;
      });

      const crc = ModbusRTU.calculateCRC(rxPayload.subarray(0, 11));
      rxPayload[11] = crc & 0xff;
      rxPayload[12] = (crc >> 8) & 0xff;

      this.terminal.log(`RX <- ${ModbusRTU.bytesToHex(rxPayload)}`, 'rx');
    }, 60);
  }

  private async pollSerial(): Promise<void> {
    const slaveId = this.state.get('slaveId');
    const profileKey = this.state.get('selectedProfileKey');
    const profile = DRIVE_PROFILES[profileKey];
    if (!profile || !this.serialManager || !this.queue) return;

    const regs = [
      profile.regFreq,
      profile.regCurrent,
      profile.regDC,
      profile.regTemp,
    ];

    const decoded: Record<number, number> = {};

    for (const reg of regs) {
      try {
        const req = ModbusRTU.buildReadRequest(slaveId, reg, 1);
        const response = await this.queue.enqueue(
          req,
          (frame) => this.serialManager!.transact(frame, 2000),
          'normal',
          2000,
          1,
        );

        const parsed = ModbusRTU.parseResponse(response);
        if (parsed.exception) {
          this.terminal.log(
            `EXC ${parsed.exception}: ${ModbusRTU.getExceptionMessage(parsed.exception)} (reg ${reg})`,
            'error',
          );
          continue;
        }

        const raw = parsed.values[0] ?? 0;
        decoded[reg] = this.decodeRegister(profile, reg, raw);
      } catch {
        // timeout/exceção - registro não lido nesta rodada
      }
    }

    if (Object.keys(decoded).length > 0) {
      this.state.updateRegisters(decoded);
      this.applyToState(profile, decoded);
    }
  }

  private decodeRegister(
    profile: DriveProfile,
    reg: number,
    raw: number,
  ): number {
    if (reg === profile.regFreq) {
      if (profile.vendor === 'SIEMENS') return (raw / 0x4000) * profile.ratedFrequency;
      if (profile.vendor === 'ROCKWELL') return raw / 100; // DSI: 0.01 Hz (xxx.xx Hz)
      return raw / 10;
    }
    if (reg === profile.regCurrent) return raw / profile.currentScale;
    if (reg === profile.regDC) return raw;
    // b027 [Drive Temp] do PF525 ja e em 1 °C
    if (reg === profile.regTemp) return profile.vendor === 'ROCKWELL' ? raw : raw / 10;
    return raw;
  }

  private applyToState(
    profile: DriveProfile,
    decoded: Record<number, number>,
  ): void {
    if (decoded[profile.regFreq] !== undefined) {
      this.state.set('currentFreq', decoded[profile.regFreq]);
    }
    if (decoded[profile.regCurrent] !== undefined) {
      this.state.set('currentAmp', decoded[profile.regCurrent]);
    }
    if (decoded[profile.regDC] !== undefined) {
      this.state.set('dcVoltage', decoded[profile.regDC]);
    }
    if (decoded[profile.regTemp] !== undefined) {
      this.state.set('heatsinkTemp', decoded[profile.regTemp]);
    }
  }
}
